import React, {useState, useEffect, useMemo} from 'react';
import {Box, Flex, useTheme, Button, VStack} from '@chakra-ui/react';
import {useNavigate} from 'react-router-dom';
import {TextA16, TextA24, TextG12, TextG14} from '../typography';
import Icon from '../Icon';
import {useInfiniteGlobalSearchWithParamsAndLocation} from '../../hooks/useFetchGlobalSearch';
import {SkeletonLoader} from '../SkeletonLoader';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

// Individual banner components
const ComicConBanner = () => {
  const navigate = useNavigate();
  // @ts-ignore - process.env is defined at build time
  const isProduction = process.env.NODE_ENV === 'production';
  const eventId = isProduction ? '57571' : '40536';

  return (
    <Box
      borderRadius="12px"
      overflow="hidden"
      position="relative"
      boxShadow="0px 4px 8px rgba(0, 0, 0, 0.1)"
      transition="transform 0.2s"
      _active={{transform: 'scale(0.98)'}}
      onClick={() => navigate(`/event/${eventId}`)}
      width="100%"
      height="200px">
      <Box
        as="img"
        src="https://images.unsplash.com/photo-1612036782180-6f0b6cd846fe?q=80&w=1920&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
        alt="Comic-Con San Diego"
        w="100%"
        h="100%"
        objectFit="cover"
        loading="eager"
      />
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="linear-gradient(135deg, rgba(255,107,53,0.3), rgba(233,30,99,0.3), rgba(156,39,176,0.3), rgba(0,188,212,0.3), rgba(255,215,0,0.3))"
        zIndex="0"
      />
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="rgba(0, 0, 0, 0.5)"
        zIndex="1"
      />
      <Box position="absolute" top="0%" width="100%" textAlign="center" zIndex="2" px={3}>
        <Box mt={4} mb={2}>
          <TextA24
            color="white"
            fontWeight="700"
            textShadow="0px 2px 4px rgba(0,0,0,0.8)"
            fontSize="28px">
            Comic-Con San Diego
          </TextA24>
          <TextA16
            color="white"
            fontWeight="600"
            textShadow="0px 1px 2px rgba(0,0,0,0.7)"
            fontSize="18px">
            The Ultimate Pop Culture Event
          </TextA16>
        </Box>
        <TextG12 color="white" fontWeight="500" textShadow="0px 1px 2px rgba(0,0,0,0.7)" mt={1}>
          July 24-27, 2025 • San Diego Convention Center
        </TextG12>
      </Box>
      <Flex
        position="absolute"
        bottom="0%"
        left="50%"
        transform="translate(-50%, -50%)"
        zIndex="2"
        flexDirection="column"
        alignItems="center"
        width="fit-content">
        <Button
          bg="linear-gradient(90deg, #FF6B35, #E91E63, #9C27B0)"
          color="white"
          borderRadius="full"
          boxShadow="0px 4px 12px rgba(0, 0, 0, 0.3)"
          _hover={{bg: 'linear-gradient(90deg, #E91E63, #9C27B0, #00BCD4)'}}
          _active={{
            transform: 'scale(0.97)',
            bg: 'linear-gradient(90deg, #E91E63, #9C27B0, #00BCD4)',
          }}
          _focus={{boxShadow: '0px 0px 0px 3px rgba(255, 107, 53, 0.4)'}}
          px={3}
          py={2}
          height="44px"
          width="100%"
          fontSize="16px"
          fontWeight="700"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/event/${eventId}`);
          }}>
          <Flex alignItems="center" gap={2} justifyContent="center">
            <Icon
              iconName="fi fi-rr-star"
              style={{
                color: 'white',
                fontSize: '18px',
              }}
            />
            <TextG14>Explore Comic-Con</TextG14>
          </Flex>
        </Button>
      </Flex>
    </Box>
  );
};

const TasteOfNorthParkBanner = () => {
  const navigate = useNavigate();
  // @ts-ignore - process.env is defined at build time
  const isProduction = process.env.NODE_ENV === 'production';
  const eventId = isProduction ? '58339' : '43881';

  return (
    <Box
      borderRadius="12px"
      overflow="hidden"
      position="relative"
      boxShadow="0px 4px 8px rgba(0, 0, 0, 0.1)"
      transition="transform 0.2s"
      _active={{transform: 'scale(0.98)'}}
      onClick={() => navigate(`/event/${eventId}`)}
      width="100%"
      height="200px">
      <Box
        as="img"
        src="https://images.unsplash.com/photo-1567620905732-2d1ec7ab7445?q=80&w=1920&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
        alt="Taste of North Park"
        w="100%"
        h="100%"
        objectFit="cover"
        loading="eager"
      />
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="linear-gradient(135deg, rgba(255,87,51,0.3), rgba(255,152,0,0.3), rgba(255,193,7,0.3), rgba(139,195,74,0.3))"
        zIndex="0"
      />
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="rgba(0, 0, 0, 0.5)"
        zIndex="1"
      />
      <Box position="absolute" top="0%" width="100%" textAlign="center" zIndex="2" px={3}>
        <Box mt={4} mb={2}>
          <TextA24
            color="white"
            fontWeight="700"
            textShadow="0px 2px 4px rgba(0,0,0,0.8)"
            fontSize="28px">
            Taste of North Park
          </TextA24>
          <TextA16
            color="white"
            fontWeight="600"
            textShadow="0px 1px 2px rgba(0,0,0,0.7)"
            fontSize="18px">
            Taste • Sip • Explore
          </TextA16>
        </Box>
        <TextG12 color="white" fontWeight="500" textShadow="0px 1px 2px rgba(0,0,0,0.7)" mt={1}>
          October 4th • 11am - 4pm • North Park
        </TextG12>
      </Box>
      <Flex
        position="absolute"
        bottom="0%"
        left="50%"
        transform="translate(-50%, -50%)"
        zIndex="2"
        flexDirection="column"
        alignItems="center"
        width="fit-content">
        <Button
          bg="linear-gradient(90deg, #FF5733, #FF9800, #FFC107)"
          color="white"
          borderRadius="full"
          boxShadow="0px 4px 12px rgba(0, 0, 0, 0.3)"
          _hover={{bg: 'linear-gradient(90deg, #E64A19, #F57C00, #FFB300)'}}
          _active={{
            transform: 'scale(0.97)',
            bg: 'linear-gradient(90deg, #E64A19, #F57C00, #FFB300)',
          }}
          _focus={{boxShadow: '0px 0px 0px 3px rgba(255, 87, 51, 0.4)'}}
          px={3}
          py={2}
          height="44px"
          width="100%"
          fontSize="16px"
          fontWeight="700"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/event/${eventId}`);
          }}>
          <Flex alignItems="center" gap={2} justifyContent="center">
            <Icon
              iconName="fi fi-rr-restaurant"
              style={{
                color: 'white',
                fontSize: '18px',
              }}
            />
            <TextG14>Explore Taste of North Park</TextG14>
          </Flex>
        </Button>
      </Flex>
    </Box>
  );
};

const FarmersMarketBanner = ({eventId}: {eventId: string}) => {
  const navigate = useNavigate();

  return (
    <Box
      borderRadius="12px"
      overflow="hidden"
      position="relative"
      boxShadow="0px 4px 8px rgba(0, 0, 0, 0.1)"
      transition="transform 0.2s"
      _active={{transform: 'scale(0.98)'}}
      onClick={() => navigate(`/event/${eventId}`)}
      width="100%"
      height="200px">
      <Box
        as="img"
        src="https://images.unsplash.com/photo-1488459716781-31db52582fe9?q=80&w=1920&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
        alt="North Park Farmers Market"
        w="100%"
        h="100%"
        objectFit="cover"
        loading="eager"
      />
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="linear-gradient(135deg, rgba(76,175,80,0.3), rgba(139,195,74,0.3), rgba(255,193,7,0.3), rgba(255,152,0,0.3))"
        zIndex="0"
      />
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="rgba(0, 0, 0, 0.4)"
        zIndex="1"
      />
      <Box position="absolute" top="0%" width="100%" textAlign="center" zIndex="2" px={3}>
        <Box mt={4} mb={2}>
          <TextA24
            color="white"
            fontWeight="700"
            textShadow="0px 2px 4px rgba(0,0,0,0.8)"
            fontSize="28px">
            North Park Farmers Market
          </TextA24>
          <TextA16
            color="white"
            fontWeight="600"
            textShadow="0px 1px 2px rgba(0,0,0,0.7)"
            fontSize="18px">
            Fresh, Local, Community
          </TextA16>
        </Box>
        <TextG12 color="white" fontWeight="500" textShadow="0px 1px 2px rgba(0,0,0,0.7)" mt={1}>
          Every Thursday • North Park
        </TextG12>
      </Box>
      <Flex
        position="absolute"
        bottom="0%"
        left="50%"
        transform="translate(-50%, -50%)"
        zIndex="2"
        flexDirection="column"
        alignItems="center"
        width="100%"
        maxWidth="fit-content">
        <Button
          bg="linear-gradient(90deg, #4CAF50, #8BC34A, #FFC107)"
          color="white"
          borderRadius="full"
          boxShadow="0px 4px 12px rgba(0, 0, 0, 0.3)"
          _hover={{bg: 'linear-gradient(90deg, #45a049, #7CB342, #FFB300)'}}
          _active={{
            transform: 'scale(0.97)',
            bg: 'linear-gradient(90deg, #45a049, #7CB342, #FFB300)',
          }}
          _focus={{boxShadow: '0px 0px 0px 3px rgba(76, 175, 80, 0.4)'}}
          px={3}
          py={2}
          height="44px"
          width="100%"
          fontSize="16px"
          fontWeight="700"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/event/${eventId}`);
          }}>
          <Flex alignItems="center" gap={2} justifyContent="center">
            <Icon
              iconName="fi fi-rr-leaf"
              style={{
                color: 'white',
                fontSize: '18px',
              }}
            />
            <TextG14>Explore the Farmers Market</TextG14>
          </Flex>
        </Button>
      </Flex>
    </Box>
  );
};

const HomePagePromoBanner = () => {
  const [farmersMarketEventId, setFarmersMarketEventId] = useState<string | null>(null);

  // @ts-ignore - process.env is defined at build time
  const isDevelopment = process.env.NODE_ENV === 'development';

  // Check if Taste of North Park should be visible (October 4th, 2025)
  const isTasteOfNorthParkVisible = useMemo(() => {
    // Always show in development for testing
    // if (isDevelopment) return true;

    const now = dayjs();
    const tasteOfNorthParkDate = dayjs('2025-10-04');
    
    // Show banner 1 day before the event until the event day
    const showStart = tasteOfNorthParkDate.subtract(1, 'day').startOf('day');
    const showEnd = tasteOfNorthParkDate.endOf('day');

    return now.isBetween(showStart, showEnd, null, '[]');
  }, [isDevelopment]);

  // Check if Comic-Con should be visible (July 24-27, 2025)
  const isComicConVisible = useMemo(() => {
    // Always show in development for testing
    //if (isDevelopment) return true;

    const now = dayjs();
    const comicConStart = dayjs('2025-07-25').startOf('day');
    const comicConEnd = dayjs('2025-07-27').endOf('day');

    return now.isBetween(comicConStart, comicConEnd, null, '[]');
  }, [isDevelopment]);

  // Check if Farmers Market should be visible (Thursdays only)
  const isFarmersMarketDay = useMemo(() => {
    // Always show in development for testing
    //if (isDevelopment) return true;

    const now = dayjs().local();
    return now.day() === 4; // Thursday = 4
  }, [isDevelopment]);

  // Search parameters for finding farmers market event
  const farmersMarketSearchParams = useMemo(() => {
    // In development, always run the search regardless of day
    // if (isDevelopment) {
    //   return {
    //     type: 'event',
    //     lat: 32.7492, // North Park coordinates
    //     long: -117.12985,
    //     term: 'north park farmers market',
    //     orderBy: 'starts_at',
    //     sort: {by: 'start_date_time', direction: 'asc'},
    //   };
    // }

    if (!isFarmersMarketDay) return null;

    return {
      type: 'event',
      lat: 32.7492, // North Park coordinates
      long: -117.12985,
      term: 'north park farmers market',
      orderBy: 'starts_at',
      sort: {by: 'start_date_time', direction: 'asc'},
    };
  }, [isDevelopment, isFarmersMarketDay]);

  // Fetch farmers market events if it's Thursday
  const fetchFarmersMarketEvents = useInfiniteGlobalSearchWithParamsAndLocation(
    farmersMarketSearchParams || {
      type: 'event',
      lat: 0,
      long: 0,
      term: '',
      disabled: true,
    }
  );

  // Find the correct farmers market event for today
  useEffect(() => {
    // In development, still perform the search but don't filter by day
    // if (isDevelopment) {
    //   if (!fetchFarmersMarketEvents.data) return;

    //   const allEvents = fetchFarmersMarketEvents.data.pages.flatMap((page) => page.data.data) || [];

    //   // In development, just find any farmers market event with the keyword
    //   const farmersMarketEvent = allEvents.find((event) => {
    //     const hasKeyword = event.description?.includes('[[northparkFarmersMarket]]');
    //     return hasKeyword;
    //   });

    //   if (farmersMarketEvent) {
    //     setFarmersMarketEventId(String(farmersMarketEvent.id));
    //   }
    //   return;
    // }

    if (!isFarmersMarketDay || !fetchFarmersMarketEvents.data) return;

    const allEvents = fetchFarmersMarketEvents.data.pages.flatMap((page) => page.data.data) || [];
    const today = dayjs().format('YYYY-MM-DD');

    // Find event happening today that contains the northparkFarmersMarket keyword
    const todaysFarmersMarket = allEvents.find((event) => {
      const eventDate = dayjs(event.start_date_time || event.starts_at).format('YYYY-MM-DD');
      const isToday = eventDate === today;
      const hasKeyword = event.description?.includes('[[northparkFarmersMarket]]');

      return isToday && hasKeyword;
    });

    if (todaysFarmersMarket) {
      setFarmersMarketEventId(String(todaysFarmersMarket.id));
    }
  }, [isDevelopment, isFarmersMarketDay, fetchFarmersMarketEvents.data]);

  // Show nothing if no events should be visible
  if (!isTasteOfNorthParkVisible && !isComicConVisible && (!isFarmersMarketDay || !farmersMarketEventId)) {
    return null;
  }

  // Check if farmers market data is still loading
  const isFarmersMarketLoading = isFarmersMarketDay && fetchFarmersMarketEvents.isLoading;

  return (
    <VStack spacing={3} mt={3} mb={3} width="100%">
      {isFarmersMarketLoading && <SkeletonLoader w="100%" height="200px" cardBorderRadius="12px" />}
      {isTasteOfNorthParkVisible && <TasteOfNorthParkBanner />}
      {isFarmersMarketDay && !isFarmersMarketLoading && farmersMarketEventId && (
        <FarmersMarketBanner eventId={farmersMarketEventId || '40536'} />
      )}
      {isComicConVisible && <ComicConBanner />}
    </VStack>
  );
};

export default HomePagePromoBanner;
